import { Link, useSearchParams } from "react-router-dom";

import { useInventory, useInventoryAnalysis } from "../api/queries";
import { SCROLLS_OFF, countsScrolls } from "../api/recipeQuery";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { adena, ago, plural, signed, when } from "../format";
import { itemHref, recipeHref } from "../search/nav";
import type { CraftFilter, CraftSort } from "./inventorySort";
import {
  filterCraftables, matchesFilter, missingSummary, sortCraftables,
} from "./inventorySort";

const SORTS: CraftSort[] = ["profit", "gain", "outlay", "covered", "item"];
const SORT_LABELS: Record<CraftSort, string> = {
  profit: "profit",
  gain: "gain from here",
  outlay: "cheapest to finish",
  covered: "closest to ready",
  item: "item",
};
const FILTERS: CraftFilter[] = ["all", "close", "ready"];
const FILTER_LABELS: Record<CraftFilter, string> = {
  all: "every recipe",
  close: "ready or one line short",
  ready: "ready now",
};

/** What the bag holds, and what it could be turned into.
 *
 *  Two panels and one button between them. The bag is cheap and always loaded;
 *  the analysis costs all 995 recipes against a price map of every name the
 *  catalogue mentions, so it runs when it is asked for — and then its state
 *  lives in the query string, so a sorted, filtered reading is a link that can
 *  be sent to someone.
 */
export function Inventory() {
  const [params, setParams] = useSearchParams();
  const sort = (SORTS.find((s) => s === params.get("sort")) ?? "profit") as CraftSort;
  const filter = (FILTERS.find((f) => f === params.get("show")) ?? "close") as CraftFilter;
  const query = params.get("q") ?? "";
  const scrolls = countsScrolls(params);
  // The button, in the URL. Not component state: an analysed view is the thing
  // worth linking to, and a reader who reloads the page should get back what
  // they were looking at rather than an unpressed button.
  const analysing = params.get("analyse") === "1";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const bag = useInventory();
  const analysis = useInventoryAnalysis(scrolls, analysing);

  if (bag.isLoading) return <><TitleBand title="Bag" /><Skeleton rows={6} /></>;
  if (bag.isError) {
    return (
      <>
        <TitleBand title="Bag" />
        <ErrorBand
          message="Could not load your inventory."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }

  const bags = bag.data?.bags ?? [];
  const pooled = bag.data?.pooled ?? [];
  const newest = bags.length ? Math.max(...bags.map((one) => one.read_at)) : null;

  if (bags.length === 0) {
    return (
      <>
        <TitleBand title="Bag" />
        <Panel primary>
          <Empty>
            No inventory has been uploaded yet. Open the Inventory window in the
            client and run <code>python -m market_analyzer inventory</code> on the
            scanning machine; it hovers every cell and sends what it reads.
          </Empty>
        </Panel>
      </>
    );
  }

  return (
    <>
      <TitleBand
        title="Bag"
        note={`${bags.map((one) => one.character).join(" · ")} · ${plural(pooled.length, "stack")}`
          + (newest === null ? "" : ` · read ${ago(newest)} ago`)}
      />

      {bag.data?.partial ? (
        <ErrorBand
          message="One of these readings may be incomplete."
          hint="The inventory grid was full to its last cell, so the bag may hold more than the window showed. Read a tab at a time, or make the window taller and recalibrate."
        />
      ) : null}

      <Panel primary>
        <div className="strip">
          <div className="group">
            <button
              type="button"
              aria-pressed={analysing}
              onClick={() => set("analyse", analysing ? "" : "1")}
            >
              {analysing ? "hide the analysis" : "run inventory analysis"}
            </button>
          </div>
          {analysing ? (
            <>
              <div className="group">
                {SORTS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={sort === option}
                    onClick={() => set("sort", option)}
                  >
                    {SORT_LABELS[option]}
                  </button>
                ))}
              </div>
              <div className="group">
                {FILTERS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={filter === option}
                    onClick={() => set("show", option)}
                  >
                    {FILTER_LABELS[option]}
                  </button>
                ))}
              </div>
              <div className="group">
                <label className="check">
                  <input
                    type="checkbox"
                    checked={scrolls}
                    onChange={() => set("scrolls", scrolls ? SCROLLS_OFF : "")}
                  />
                  <span>count recipe scrolls</span>
                </label>
              </div>
              <input
                type="search"
                value={query}
                placeholder="filter by item"
                aria-label="filter recipes by item"
                onChange={(event) => set("q", event.target.value)}
              />
            </>
          ) : null}
        </div>

        {analysing ? <Analysis
          state={analysis}
          sort={sort}
          filter={filter}
          query={query}
        /> : null}
      </Panel>

      <Panel>
        <p className="scan-meta">
          Every bag added together — what the analysis is costed against.
        </p>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Quantity</th>
                <th>Where</th>
                <th className="num">Vendor price</th>
              </tr>
            </thead>
            <tbody>
              {pooled.map((stack) => (
                <tr key={`${stack.item}-${stack.enchant ?? ""}`}>
                  <td>
                    {stack.item_id === null ? stack.item : (
                      <Link to={itemHref(stack.item_id, stack.item)}>{stack.item}</Link>
                    )}
                    {stack.enchant ? <span className="dim"> +{stack.enchant}</span> : null}
                  </td>
                  <td className="num">{adena(stack.qty)}</td>
                  <td className="dim">{holders(bags, stack.item)}</td>
                  {/* What an NPC pays, from the tooltip. Dim, because it is a
                      floor rather than a price: it is what the item is worth
                      when nobody is buying. */}
                  <td className="num dim">{adena(stack.npc_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel>
        <p className="scan-meta">
          {plural(bags.length, "bag")} — one reading per character, replaced each
          time that character's inventory is scanned.
        </p>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Character</th>
                <th className="num">Stacks</th>
                <th className="num">Slots</th>
                <th>Read</th>
                <th>Complete</th>
              </tr>
            </thead>
            <tbody>
              {bags.map((one) => (
                <tr key={one.character}>
                  <td>{one.character}</td>
                  <td className="num">{one.stacks.length}</td>
                  {/* The window's own count, worn gear included: it is what
                      the client said, and nothing subtracts it. */}
                  <td className="num dim">
                    {one.taken === null ? "—" : `${one.taken}/${one.capacity}`}
                  </td>
                  <td className="dim">{when(one.read_at)}</td>
                  <td className="dim">{one.partial ? "may be partial" : "whole bag"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

/** Which characters are carrying an item, for the pooled table's "where"
 *  column. The pool is what the analysis uses; this is how a reader finds out
 *  which of their characters to walk to. */
function holders(bags: { character: string; stacks: { item: string }[] }[], item: string): string {
  const needle = item.toLowerCase();
  return bags
    .filter((bag) => bag.stacks.some((stack) => stack.item.toLowerCase() === needle))
    .map((bag) => bag.character)
    .join(", ");
}

interface AnalysisProps {
  state: ReturnType<typeof useInventoryAnalysis>;
  sort: CraftSort;
  filter: CraftFilter;
  query: string;
}

function Analysis({ state, sort, filter, query }: AnalysisProps) {
  if (state.isPending) return <Skeleton rows={8} />;
  if (state.isError) {
    return (
      <ErrorBand
        message="Could not cost the catalogue against your bag."
        hint="The server may be restarting. Try the button again in a moment."
      />
    );
  }

  const all = state.data?.recipes ?? [];
  const shown = sortCraftables(
    filterCraftables(all, query).filter((row) => matchesFilter(row, filter)),
    sort,
  );
  const ready = all.filter((row) => row.ready).length;

  if (shown.length === 0) {
    return (
      <Empty>
        {query
          ? "No recipe matches that."
          : `Nothing in that band. ${plural(ready, "recipe")} can be crafted from what you carry.`}
      </Empty>
    );
  }

  return (
    <>
      <p className="scan-meta">
        Profit prices every material at the market, the ones already in your bag
        included — a Stem you own is a Stem you could have sold. It is struck
        against “Buys for”, not “Sells for”: the ask is what you hope somebody
        pays, the bid is what a buyer has already committed to, and a craft
        nobody is bidding on has no profit at all rather than a profit of zero.
        “To buy” is only what is still missing, so a row that is ready costs
        nothing more to finish. Coverage counts the recipe’s own material lines:
        a material you could make from something else you carry is still counted
        as missing, though its price follows the cheaper of buying and making it.
        {` ${plural(shown.length, "recipe")} shown; ${ready} in the catalogue are ready now.`}
      </p>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Craft</th>
              <th className="num">Have</th>
              <th className="num">Can make</th>
              <th>Still needed</th>
              <th className="num">To buy</th>
              <th className="num">Sells for</th>
              <th className="num">Buys for</th>
              <th className="num">Profit</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.id}>
                <td>
                  <Link to={recipeHref(row.id)}>{row.craft_item}</Link>
                  {row.count > 1 ? <span className="dim"> ×{row.count}</span> : null}
                  {row.craft_item_id === null ? null : (
                    <>
                      {" "}
                      <Link className="dim" to={itemHref(row.craft_item_id, row.craft_item)}>
                        market
                      </Link>
                    </>
                  )}
                </td>
                {/* The coverage, as a fraction and as a word. The hue budget is
                    spent -- olive is a bid, rose an ask, gold adena -- so
                    "ready" is spelled out rather than coloured. */}
                <td className={`num ${row.ready ? "" : "dim"}`}>
                  {row.covered}/{row.lines}
                  {row.ready ? <span className="ready"> ready</span> : null}
                </td>
                {/* Whole crafts the bag supports. An em dash rather than a 0:
                    none is not a quantity. */}
                <td className="num dim">{row.makeable > 0 ? row.makeable : "—"}</td>
                <td className="dim">
                  {missingSummary(row)}
                  {row.outlay_unpriced > 0
                    ? ` · ${row.outlay_unpriced} nobody prices`
                    : null}
                </td>
                {/* What finishing this costs from here. A zero is the good
                    case, and it reads as one because the coverage column
                    beside it says the lines are covered. */}
                <td className="num adena">{adena(row.outlay)}</td>
                {/* The batch both ways, side by side and directly comparable:
                    both are multiplied by the yield, so the pair is the spread
                    on 500 arrows rather than on one. Rose is the ask and olive
                    the bid, the same two hues the recipes page spends. The
                    column on the right is struck against this one. */}
                <td className="num sell">{adena(row.revenue)}</td>
                <td className="num buy">{adena(row.proceeds)}</td>
                <td className={`num ${row.profit === null ? "dim" : row.profit < 0 ? "buy" : "sell"}`}>
                  {signed(row.profit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
